import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, Delete, Fingerprint, LogIn, LogOut, Check } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import EarlyExitForm from "@/components/EarlyExitForm";

function beep(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine";
    if (type === "success") { o.frequency.value = 880; g.gain.value = 0.15; }
    else { o.frequency.value = 220; g.gain.value = 0.2; }
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, type === "success" ? 220 : 400);
  } catch {}
}

export default function PinEntry() {
  const [params] = useSearchParams();
  const deviceId = params.get("device") || "";
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [earlyExit, setEarlyExit] = useState(null); // {attendance_id, name}
  const [now, setNow] = useState(new Date());
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 5000 }
      );
    }
  }, []);

  const submit = async (finalPin) => {
    if (busy || finalPin.length < 4) return;
    setBusy(true);
    // How long the result overlay stays on screen. Longer for quick-exit
    // warnings so the personnel can actually read them.
    let dismissAfter = 3500;
    try {
      const { data } = await api.post("/attendance/pin-checkin", {
        pin: finalPin,
        device_id: deviceId || undefined,
        lat: coords?.lat,
        lng: coords?.lng,
      });
      setResult({ ok: true, ...data });
      beep("success");
      if (data.quick_exit) {
        // Play a second, slightly longer warning beep to draw attention.
        setTimeout(() => beep("error"), 400);
        dismissAfter = 6500;
      }
      // On any checkout, prompt for reason + approver (skippable).
      if (data.status === "checkout" && data.attendance_id) {
        setTimeout(() => {
          setEarlyExit({
            attendance_id: data.attendance_id,
            name: `${data.employee.first_name} ${data.employee.last_name}`,
            early: (data.early_leave_minutes || 0) > 0,
          });
        }, dismissAfter);
      }
    } catch (e) {
      setResult({ ok: false, message: formatApiErrorDetail(e.response?.data?.detail) || "Bir hata oluştu" });
      beep("error");
    } finally {
      setTimeout(() => { setResult(null); setPin(""); setBusy(false); }, dismissAfter);
    }
  };

  const press = (d) => {
    if (result) return;
    const next = (pin + d).slice(0, 8);
    setPin(next);
    if (next.length === 6) submit(next);
  };
  const backspace = () => setPin((p) => p.slice(0, -1));
  const clear = () => setPin("");

  return (
    <div className="fixed inset-0 z-50 bg-[#050508] text-white overflow-hidden flex flex-col" data-testid="pin-entry-page">
      <div className="absolute top-0 left-0 right-0 z-20 p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo/qrpts-logo.png" alt="QRPTS" className="w-11 h-11 object-contain" />
          <div>
            <div className="font-display font-bold text-base tracking-tight">QRPTS</div>
            <div className="text-xs text-white/50">PIN Girişi · {deviceId || "Cihaz belirsiz"}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-bold tabular-nums">{now.toLocaleTimeString("tr-TR")}</div>
          <div className="text-xs text-white/50">{now.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 pt-24">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-bold tracking-tight text-center mb-2">Personel PIN</h1>
          <p className="text-white/60 text-sm text-center mb-8">6 haneli PIN kodunuzu girin</p>

          {/* PIN dots */}
          <div className="flex justify-center gap-3 mb-8" data-testid="pin-dots">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full border-2 transition-colors duration-150 ${
                  pin.length > i ? "bg-blue-500 border-blue-500" : "border-white/25"
                }`}
              />
            ))}
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => press(String(n))}
                disabled={busy || !!result}
                data-testid={`pin-key-${n}`}
                className="h-16 rounded-xl bg-white/5 border border-white/10 font-display text-2xl font-bold hover:bg-white/10 active:scale-95 transition-transform duration-100 disabled:opacity-50"
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              onClick={clear}
              disabled={busy || !!result}
              data-testid="pin-clear"
              className="h-16 rounded-xl bg-white/5 border border-white/10 text-xs font-semibold text-white/60 hover:bg-white/10 active:scale-95 transition-transform duration-100"
            >
              SİL
            </button>
            <button
              type="button"
              onClick={() => press("0")}
              disabled={busy || !!result}
              data-testid="pin-key-0"
              className="h-16 rounded-xl bg-white/5 border border-white/10 font-display text-2xl font-bold hover:bg-white/10 active:scale-95 transition-transform duration-100 disabled:opacity-50"
            >
              0
            </button>
            <button
              type="button"
              onClick={backspace}
              disabled={busy || !!result}
              data-testid="pin-backspace"
              className="h-16 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white/10 active:scale-95 transition-transform duration-100"
            >
              <Delete className="w-5 h-5" />
            </button>
          </div>

          {pin.length >= 4 && pin.length < 6 && (
            <Button
              onClick={() => submit(pin)}
              disabled={busy}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-500 h-12 rounded-xl font-semibold"
              data-testid="pin-submit"
            >
              Onayla
            </Button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {result && (() => {
          const isCheckin = result.ok && result.status === "checkin";
          const isCheckout = result.ok && result.status === "checkout";
          const isCompleted = result.ok && result.status === "completed";
          const isQuickExit = isCheckout && result.quick_exit;
          // Quick-exit uses an amber tone to make the accidental-scan warning
          // visually distinct from a normal blue checkout.
          const bgTone = isCheckin ? "bg-emerald-500/25"
            : isQuickExit ? "bg-amber-500/25"
            : isCheckout ? "bg-blue-500/25"
            : isCompleted ? "bg-amber-500/25"
            : "bg-red-500/25";
          const borderTone = isCheckin ? "border-emerald-500"
            : isQuickExit ? "border-amber-500"
            : isCheckout ? "border-blue-500"
            : isCompleted ? "border-amber-500"
            : "border-red-500";
          const iconColor = isCheckin ? "text-emerald-400"
            : isQuickExit ? "text-amber-400"
            : isCheckout ? "text-blue-400"
            : isCompleted ? "text-amber-400"
            : "text-red-400";
          const bigLabel = isCheckin ? "GİRİŞ"
            : isCheckout ? "ÇIKIŞ"
            : isCompleted ? "TAMAMLANDI"
            : "HATA";
          const Ico = isCheckin ? LogIn
            : isCheckout ? LogOut
            : isCompleted ? Check
            : XCircle;
          return (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className={`absolute inset-0 z-30 backdrop-blur-xl flex items-center justify-center p-8 ${bgTone}`}
              data-testid="pin-result"
            >
              <motion.div
                initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }}
                className={`rounded-3xl p-10 text-center max-w-md w-full border-4 ${borderTone}`}
                style={{ background: "rgba(20,20,26,0.96)", backdropFilter: "blur(24px)" }}
                data-status={result.status || (result.ok ? "ok" : "error")}
                data-quick-exit={isQuickExit ? "true" : "false"}
              >
                {/* Huge action label */}
                <div className={`font-display font-black tracking-tight text-5xl mb-4 ${iconColor}`}>
                  {bigLabel}
                </div>
                <Ico className={`w-28 h-28 mx-auto mb-4 ${iconColor}`} strokeWidth={2.5} />

                {result.employee && (
                  <>
                    {result.employee.photo_url ? (
                      <img src={result.employee.photo_url} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border-2 border-white/20" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-white/10 mx-auto mb-3 flex items-center justify-center font-display font-bold text-2xl">
                        {result.employee.first_name?.[0]}{result.employee.last_name?.[0]}
                      </div>
                    )}
                    <div className="font-display text-2xl font-bold tracking-tight">
                      {result.employee.first_name} {result.employee.last_name}
                    </div>
                    <div className="text-white/60 text-sm mt-1 font-mono">#{result.employee.employee_no}</div>
                  </>
                )}
                <div className={`mt-4 text-xl font-medium ${iconColor}`}>{result.message}</div>
                {result.time && (
                  <div className="text-white font-mono text-2xl mt-3 tabular-nums font-bold">
                    {new Date(result.time).toLocaleTimeString("tr-TR", {hour: "2-digit", minute: "2-digit", second: "2-digit"})}
                  </div>
                )}
                {isQuickExit && (
                  <div
                    className="mt-4 rounded-xl border-2 border-amber-400 bg-amber-500/15 px-4 py-3 text-left"
                    data-testid="quick-exit-warning"
                  >
                    <div className="font-display font-bold text-amber-300 text-base tracking-tight">
                      ⚠️ ÇIKIŞ Kaydedildi
                    </div>
                    <div className="text-amber-100/90 text-sm mt-1 leading-snug">
                      Girişten yalnızca <b>{result.worked_minutes || 0} dk</b> sonra
                      çıkış yaptınız. Yanlış okuttuysanız yöneticiyle iletişime geçin.
                    </div>
                  </div>
                )}
                {result.late_minutes > 0 && (
                  <div className="text-amber-400 mt-2 text-sm">{result.late_minutes} dakika geç</div>
                )}
                {result.overtime_minutes > 0 && (
                  <div className="text-blue-400 mt-2 text-sm">Fazla mesai: {result.overtime_minutes} dk</div>
                )}
                {result.worked_minutes > 0 && !isQuickExit && (
                  <div className="text-white/70 mt-2 text-sm">
                    Toplam çalışma: {Math.floor(result.worked_minutes / 60)}sa {result.worked_minutes % 60}dk
                  </div>
                )}
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {earlyExit && (
        <EarlyExitForm
          attendanceId={earlyExit.attendance_id}
          employeeName={earlyExit.name}
          earlyExit={earlyExit.early}
          onDone={() => setEarlyExit(null)}
        />
      )}
    </div>
  );
}
